import React from "react";
import Footer from "../Footer";
import Navbar from "../Navbar";
import SEO from "../SEO";
import { FaUserTimes, FaLock, FaHistory, FaCheckCircle, FaExclamationCircle } from "react-icons/fa";

const AccountDeletionPolicy: React.FC = () => {
    const policySections = [
        {
            title: "1. Your Right to Privacy",
            icon: <FaUserTimes />,
            content: `At ${import.meta.env.VITE_COMPANY_NAME}, you are in full control of your digital footprint. You have the right to request the complete deletion of your account and associated personal data at any time, subject to regulatory retention requirements.`,
        },
        {
            title: "2. Simple Deletion Process",
            icon: <FaCheckCircle />,
            content: `We've made it easy. You can initiate a deletion request directly through your account settings in our mobile app or by sending a secure message from your registered email to ${import.meta.env.VITE_COMPANY_EMAIL}.`,
        },
        {
            title: "3. Responsible Retention",
            icon: <FaLock />,
            content: "To comply with Indian financial regulations (RBI/FIU), certain records related to loan transactions and identity verification must be retained for a mandated period. All other non-essential data will be permanently purged within 30 days.",
        },
        {
            title: "4. Important Considerations",
            icon: <FaExclamationCircle />,
            list: [
                "Settlement: All active loan obligations must be fully settled before an account can be closed.",
                "Access: Deletion is permanent and results in the immediate loss of access to your transaction history.",
                "Security: We verify every deletion request to protect you from unauthorized account closures.",
            ],
        },
    ];

    return (
        <div className="min-h-screen bg-white text-text-primary px-0 mx-0 overflow-x-hidden font-sans selection:bg-secondary/10 selection:text-secondary">
            <SEO 
                title="Account Deletion Policy"
                description={`Simple and transparent account closure guidelines at ${import.meta.env.VITE_COMPANY_NAME}. Understand your rights and how we handle your data.`}
                keywords={`account deletion, data privacy, fintech rights, user control`}
            />
            <Navbar />

            <main className="relative z-10 pt-44 pb-20 md:pb-24">
                <div className="max-w-4xl mx-auto px-6 lg:px-12">
                    
                    <header className="mb-24">
                        <div className="inline-flex items-center gap-3 px-6 py-2 bg-white border border-secondary/20 shadow-xl rounded-full mb-10 transition-transform hover:scale-105">
                            <div className="w-2 h-2 bg-secondary rounded-full animate-pulse" />
                            <span className="text-[10px] font-black tracking-[0.3em] text-text-secondary uppercase">Privacy First</span>
                        </div>
                        <h1 className="text-5xl md:text-8xl font-black tracking-tighter leading-[0.9] mb-10 text-text-primary">
                            Your Data. <br />
                            <span className="text-secondary">Your Choice.</span>
                        </h1>
                        <p className="text-text-secondary text-lg md:text-2xl font-medium leading-relaxed max-w-2xl">
                            We value your trust above all else. If you decide to move on, we ensure your account deletion is handled with complete transparency and security.
                        </p>
                    </header>

                    <div className="space-y-12">
                        {policySections.map((section, idx) => (
                            <div key={idx} className="bg-white p-12 md:p-16 rounded-[4rem] border border-gray-100 shadow-xl relative overflow-hidden transition-all hover:shadow-2xl">
                                <div className="flex flex-col md:flex-row gap-10">
                                    <div className="w-16 h-16 bg-gray-50 rounded-2xl flex items-center justify-center text-primary text-3xl shrink-0">
                                        {section.icon}
                                    </div>
                                    <div className="flex-1">
                                        <h2 className="text-2xl md:text-4xl font-black mb-8 text-text-primary tracking-tight">
                                            {section.title}
                                        </h2>
                                        {section.content && (
                                            <p className="text-text-secondary text-lg md:text-xl leading-relaxed font-medium mb-6">
                                                {section.content}
                                            </p>
                                        )}
                                        {section.list && (
                                            <ul className="space-y-4 pt-4">
                                                {section.list.map((li, i) => (
                                                    <li key={i} className="flex items-start gap-4 text-text-secondary font-medium text-lg">
                                                        <div className="w-2 h-2 bg-secondary mt-2.5 rounded-full shrink-0" />
                                                        <span>{li}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Final Security Note */}
                    <div className="mt-20 p-12 bg-primary text-white rounded-[3rem] shadow-2xl relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-[80px]" />
                        <h3 className="text-2xl font-black mb-6 flex items-center gap-3">
                            <FaHistory className="text-secondary text-lg" />
                            Data Retention Standards
                        </h3>
                        <p className="text-white/80 text-lg leading-relaxed font-medium">
                            Our retention policies are strictly aligned with RBI and PMLA guidelines. We only hold what is legally required, ensuring that your privacy is protected even after you close your account with {import.meta.env.VITE_COMPANY_NAME}.
                        </p>
                    </div>

                </div>
            </main>

            <Footer />
        </div>
    );
};

export default AccountDeletionPolicy;
